// src/design/designCenter.jsx
// Global Design Center modal - draggable, resizable, with tabbed interface.
// Mirrors chatcenter.jsx functionality but with a single panel body and tabs.

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { FaTimes, FaWindowMaximize, FaWindowRestore } from 'react-icons/fa';
import { useModalStore } from '../state/modalStore';
import { useProfileStore } from '../state/profileStore';
import DesignCenterWelcome from './DesignCenterWelcome';
import AdminDesignCenterReview from '../components/admin/AdminDesignCenterReview';
import DesignCenterWorkspace from './DesignCenterWorkspace';

const API_URL = process.env.REACT_APP_API_URL;

export default function DesignCenter() {
  const { designCenterOpen, closeDesignCenter } = useModalStore();
  const bringToFront = useModalStore((s) => s.bringToFront);
  const designCenterZ = useModalStore((s) => s.getZIndex('designCenter'));

  // Check if user has acknowledged the Design Center welcome
  const profile = useProfileStore((s) => s.profile);
  const updateProfile = useProfileStore((s) => s.updateProfile);
  const hasAcknowledged = profile?.u_design_center_acknowledged;
    // Admin-only visibility (THIS PHASE: u_id=109 + u_is_admin=true)
  const isDesignCenterAdmin = profile?.u_id === 109 && profile?.u_is_admin === true;
  const [showWelcome, setShowWelcome] = useState(false);

  const [showAdminReview, setShowAdminReview] = useState(false);
  const isAdminViewer = profile?.u_id === 109 && profile?.u_is_admin === true;

  // Active tab (must be declared before callbacks that reference it)
  const [activeTab, setActiveTab] = useState(null); // no default tab

  // Profession gate state
  const [professionChecked, setProfessionChecked] = useState(false);
  const [hasProfession, setHasProfession] = useState(false);
  const [professionName, setProfessionName] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');

  // Creation limit counter
  const [creationInfo, setCreationInfo] = useState(null); // { limit, activeCreationCount, tier }

  // Tab switch confirmation state
  const [hasActiveWork, setHasActiveWork] = useState(false);
  const [pendingTabId, setPendingTabId] = useState(null);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);

  // Close confirmation state
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Fetch creation limit status when Design Center opens
  useEffect(() => {
    if (!designCenterOpen) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription/status`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) setCreationInfo({ limit: data.limit, activeCreationCount: data.activeCreationCount, tier: data.tier });
      } catch {}
    })();
  }, [designCenterOpen]);

  // Check profession on mount / when Design Center opens
  useEffect(() => {
    if (!designCenterOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/check-profession`, { credentials: 'include' });
        const data = await res.json();
        if (cancelled) return;
        if (data.ok && data.hasProfession) {
          setHasProfession(true);
          setProfessionName(data.professionName || '');
        } else {
          setHasProfession(false);
          setProfessionName('');
        }
      } catch (err) {
        console.error('[designcenter] check-profession error:', err);
        if (!cancelled) setHasProfession(false);
      } finally {
        if (!cancelled) setProfessionChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [designCenterOpen]);

  // Submit profession from inline prompt
  const handleResolveProfession = useCallback(async () => {
    if (!industryInput.trim()) return;
    setResolving(true);
    setResolveError('');
    try {
      const res = await fetch(`${API_URL}/api/designcenter/resolve-profession`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ industryText: industryInput.trim() }),
      });
      const data = await res.json();
      if (data.ok && data.resolved) {
        setHasProfession(true);
        setProfessionName(data.professionName || '');
        // Hot-load into profile store so the profile page updates immediately
        updateProfile({ b_business_industry: industryInput.trim() });
      } else if (data.ok && !data.resolved) {
        setResolveError(data.message || 'Could not determine your profession. Please be more specific.');
      } else {
        setResolveError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('[designcenter] resolve-profession error:', err);
      setResolveError('Network error. Please try again.');
    } finally {
      setResolving(false);
    }
  }, [industryInput]);

  // Tab click handler — with confirmation if active work exists
  const handleTabClick = useCallback((tabId) => {
    if (tabId === activeTab) return;
    if (hasActiveWork) {
      setPendingTabId(tabId);
      setShowSwitchConfirm(true);
    } else {
      setActiveTab(tabId);
    }
  }, [activeTab, hasActiveWork]);

  const confirmTabSwitch = useCallback(() => {
    setShowSwitchConfirm(false);
    setHasActiveWork(false);
    if (pendingTabId !== null) {
      setActiveTab(pendingTabId);
      setPendingTabId(null);
    }
  }, [pendingTabId]);

  const cancelTabSwitch = useCallback(() => {
    setShowSwitchConfirm(false);
    setPendingTabId(null);
  }, []);

  // Close modal handler — warn if active work exists
  const handleCloseClick = useCallback(() => {
    if (hasActiveWork) {
      setShowCloseConfirm(true);
    } else {
      closeDesignCenter();
    }
  }, [hasActiveWork, closeDesignCenter]);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    setHasActiveWork(false);
    closeDesignCenter();
  }, [closeDesignCenter]);

  const cancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  // Show welcome modal when Design Center opens and user hasn't acknowledged.
  // Also force it open if user has no profession — they must go through the full
  // onboarding flow (profession toast on top → welcome underneath).
  useEffect(() => {
    if (designCenterOpen && !hasAcknowledged) {
      setShowWelcome(true);
    }
  }, [designCenterOpen, hasAcknowledged]);

  // If profession check completes and user has no profession, force welcome open
  // even if they previously acknowledged (they need to redo onboarding)
  useEffect(() => {
    if (professionChecked && !hasProfession && designCenterOpen) {
      setShowWelcome(true);
    }
  }, [professionChecked, hasProfession, designCenterOpen]);

  const handleWelcomeAcknowledge = () => {
    setShowWelcome(false);
  };

  const modalRef = useRef(null);
  const [windowMode, setWindowMode] = useState('norm');

  const [windowRect, setWindowRect] = useState(() => {
    const pad = 12;
    const w = Math.min(900, Math.floor(window.innerWidth * 0.85));
    const h = Math.floor(window.innerHeight * 0.85);
    return {
      width: w,
      height: h,
      x: Math.max(pad, Math.floor((window.innerWidth - w) / 2)),
      y: Math.max(pad, Math.floor((window.innerHeight - h) / 2)),
    };
  });

  const getMaxRect = useCallback(() => {
    const pad = 12;
    return {
      x: pad,
      y: pad,
      width: Math.max(600, window.innerWidth - pad * 2),
      height: Math.max(400, window.innerHeight - pad * 2),
    };
  }, []);

  const toggleMaximize = useCallback(() => {
    setWindowMode((prev) => (prev === 'max' ? 'norm' : 'max'));
  }, []);

  const onMinimizeClick = useCallback(() => {
    if (windowMode === 'max') setWindowMode('norm');
  }, [windowMode]);

  // Tab labels + folder keys (folder key maps to filesystem folder name)
  const tabs = [
    { id: 1, label: 'How Things Work', folderKey: 'how_things_work' },
    { id: 2, label: 'How-To & Tips', folderKey: 'how_to_tips' },
    { id: 3, label: 'Problems & Solutions', folderKey: 'problems_solutions' },
    { id: 4, label: 'Advice & Insights', folderKey: 'advice_insights' },
    { id: 5, label: 'Specials & Discounts', folderKey: 'specials_discounts' },
    { id: 6, label: 'Stories & Experiences', folderKey: 'stories_experiences' },
    { id: 7, label: 'Community Relations', folderKey: 'community_relations' },
    { id: 8, label: 'Humor & Personality', folderKey: 'humor_personality' },
  ];

  // Goal cards data
  const goalCards = [
    {
      id: 1,
      title: 'Give Them Knowledge',
      subtitle: 'Educate them and they will remember you. Give them "Tips" and "How to" examples. Help them navigate your industry when that time comes.',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      links: [
        { tabId: 1, label: 'How Things Work' },
        { tabId: 2, label: 'How-To & Tips' },
      ],
icon: (
  <div className="w-14 h-14 rounded-full bg-green-200/40 flex items-center justify-center">
    <svg
      viewBox="0 0 100 77.036"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
      className="w-11 h-11"
    >
      <circle fill="#1a774b" cx="50" cy="3.125" r="3.125" />
      <circle fill="#1a774b" cx="40.021" cy="4.952" r="3.125" />
      <circle fill="#1a774b" cx="31.287" cy="9.998" r="3.125" />
      <circle fill="#1a774b" cx="24.803" cy="17.725" r="3.125" />

      <path
        fill="#1a774b"
        d="M45.67,53.313c0-0.004-0.002-0.005-0.004-0.01l-4.022-10.059c-0.249-11.273-9.479-20.373-20.811-20.373C9.347,22.872,0,32.219,0,43.705c0,5.812,2.398,11.069,6.25,14.853v18.479h25v-7.248c5.743,0,9.375-3.675,9.375-9.419v-4.166h3.125c1.151,0,2.083-0.933,2.083-2.084C45.833,53.863,45.67,53.313,45.67,53.313z M12.5,52.039c-1.725,0-3.125-1.4-3.125-3.125c0-1.726,1.4-3.125,3.125-3.125s3.125,1.399,3.125,3.125C15.625,50.639,14.225,52.039,12.5,52.039z M12.5,43.705c-1.725,0-3.125-1.399-3.125-3.125c0-1.725,1.4-3.125,3.125-3.125s3.125,1.399,3.125,3.125C15.625,42.306,14.225,43.705,12.5,43.705z M20.833,52.039c-1.726,0-3.125-1.4-3.125-3.125c0-1.726,1.399-3.125,3.125-3.125c1.725,0,3.125,1.399,3.125,3.125C23.958,50.639,22.559,52.039,20.833,52.039z M20.833,43.705c-1.726,0-3.125-1.399-3.125-3.125c0-1.725,1.399-3.125,3.125-3.125c1.725,0,3.125,1.399,3.125,3.125C23.958,42.306,22.559,43.705,20.833,43.705z M20.833,35.372c-1.726,0-3.125-1.4-3.125-3.125s1.399-3.125,3.125-3.125c1.725,0,3.125,1.4,3.125,3.125S22.559,35.372,20.833,35.372z M29.167,52.039c-1.725,0-3.125-1.4-3.125-3.125c0-1.726,1.4-3.125,3.125-3.125c1.726,0,3.125,1.399,3.125,3.125C32.292,50.639,30.892,52.039,29.167,52.039z M29.167,43.705c-1.725,0-3.125-1.399-3.125-3.125c0-1.725,1.4-3.125,3.125-3.125c1.726,0,3.125,1.399,3.125,3.125C32.292,42.306,30.892,43.705,29.167,43.705z"
      />

      <circle fill="#1a774b" cx="59.855" cy="4.952" r="3.125" />
      <circle fill="#1a774b" cx="68.59" cy="9.998" r="3.125" />
      <circle fill="#1a774b" cx="75.073" cy="17.725" r="3.125" />

      <path
        fill="#1a774b"
        d="M79.167,22.872c-11.333,0-20.562,9.1-20.812,20.373l-4.021,10.059c-0.002,0.005-0.005,0.006-0.005,0.01c0,0-0.162,0.55-0.162,0.806c0,1.151,0.932,2.084,2.083,2.084h3.125v4.166c0,5.744,3.632,9.419,9.375,9.419v7.248h25V58.558c3.852-3.783,6.25-9.04,6.25-14.853C100,32.219,90.653,22.872,79.167,22.872z M79.167,52.039c-1.726,0-3.125-1.4-3.125-3.125c0-1.726,1.399-3.125,3.125-3.125c1.725,0,3.125,1.399,3.125,3.125C82.292,50.639,80.892,52.039,79.167,52.039z M79.167,43.705c-1.726,0-3.125-1.399-3.125-3.125c0-1.725,1.399-3.125,3.125-3.125c1.725,0,3.125,1.399,3.125,3.125C82.292,42.306,80.892,43.705,79.167,43.705z M79.167,35.372c-1.726,0-3.125-1.4-3.125-3.125s1.399-3.125,3.125-3.125c1.725,0,3.125,1.4,3.125,3.125S80.892,35.372,79.167,35.372z"
      />
    </svg>
  </div>
),

    },
    {
      id: 2,
      title: 'Build Trust & Authority',
      subtitle: 'Show them problems they may face. Show them how you can solve them. Give them before and after examples. Help them make the right decisions. ',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      links: [
        { tabId: 3, label: 'Problems & Solutions' },
        { tabId: 4, label: 'Advice & Insights' },
      ],
      icon: (
        <svg viewBox="0 0 64 64" className="w-14 h-14">
          {/* Shield with checkmark */}
          <circle cx="32" cy="32" r="28" fill="#dbeafe" />
          <path d="M32 8 L52 18 L52 32 C52 46 32 56 32 56 C32 56 12 46 12 32 L12 18 Z" fill="#3b82f6" />
          <path d="M24 32 L30 38 L42 26" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="52" cy="48" r="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
          <path d="M49 48 L51 50 L55 46" stroke="#3b82f6" strokeWidth="2" fill="none" />
        </svg>
      ),
    },
    {
      id: 3,
      title: 'Promote Your Brand',
      subtitle: 'Design Specials, offers, discounts and exclusive coupons to attract a new set of clients from your teammates channels. Keep it sane & profitable!',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      links: [
        { tabId: 5, label: 'Specials & Discounts' },
      ],
      icon: (
        <svg viewBox="0 0 64 64" className="w-14 h-14">
          {/* Bullhorn/megaphone with star burst */}
          <circle cx="32" cy="32" r="28" fill="#f3e8ff" />
          <path d="M18 26 L38 18 L38 46 L18 38 Z" fill="#a855f7" />
          <ellipse cx="15" cy="32" rx="5" ry="7" fill="#a855f7" />
          <path d="M38 22 L46 18 L46 46 L38 42" fill="#9333ea" />
          <circle cx="50" cy="16" r="3" fill="#c084fc" />
          <circle cx="54" cy="26" r="2" fill="#c084fc" />
          <circle cx="52" cy="38" r="2.5" fill="#c084fc" />
          <path d="M10 40 L8 50 L14 50 L16 40" fill="#7c3aed" />
          <text x="26" y="36" fontSize="10" fill="white" fontWeight="bold">$$</text>
        </svg>
      ),
    },
    {
      id: 4,
      title: 'Increase Engagement',
      subtitle: 'Create posts that people will like, comment on, and share. Relatable stories, real life experiences. Community involvement and awareness.',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      links: [
        { tabId: 6, label: 'Stories & Experiences' },
        { tabId: 7, label: 'Community Relations' },
      ],
      icon: (
        <svg viewBox="0 0 64 64" className="w-14 h-14">
          {/* Chat bubbles with hearts and thumbs up */}
          <circle cx="32" cy="32" r="28" fill="#ffedd5" />
          <rect x="12" y="20" rx="8" ry="8" width="28" height="20" fill="#f97316" />
          <path d="M20 40 L16 48 L24 40" fill="#f97316" />
          <path d="M26 28 C26 24 30 24 30 28 C30 24 34 24 34 28 C34 34 26 36 26 36 C26 36 26 34 26 28" fill="#ef4444" />
          <rect x="32" y="28" rx="6" ry="6" width="22" height="16" fill="#fb923c" />
          <path d="M44 44 L48 50 L40 44" fill="#fb923c" />
          <path d="M40 38 L44 34 L48 38" stroke="white" strokeWidth="2" fill="none" />
        </svg>
      ),
    },
    {
      id: 5,
      title: 'Show Your Personality',
      subtitle: 'Let them know who you are. Use humorous, behind-the-scenes, relatable content. Develop a meme or two. Show them the human side of your business.',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      links: [
        { tabId: 8, label: 'Humor & Personality' },
      ],
      icon: (
        <svg viewBox="0 0 64 64" className="w-14 h-14">
          {/* Person with tools */}
          <circle cx="32" cy="32" r="28" fill="#fef3c7" />
          <circle cx="28" cy="24" r="10" fill="#f59e0b" />
          <ellipse cx="28" cy="48" rx="14" ry="10" fill="#f59e0b" />
          <rect x="44" y="20" width="4" height="24" rx="2" fill="#78716c" />
          <circle cx="46" cy="18" r="4" fill="#78716c" />
          <rect x="50" y="32" width="8" height="4" rx="1" fill="#78716c" />
          <rect x="54" y="28" width="4" height="12" rx="1" fill="#a8a29e" />
        </svg>
      ),
    },
  ];

  if (!designCenterOpen) return null;

  return (
    <>
      {/* Welcome Modal - shows once per user */}
      {showWelcome && (
        <DesignCenterWelcome onAcknowledge={handleWelcomeAcknowledge} />
      )}

      {/* Profession Toast — floats on top of Welcome modal when user has no profession */}
      {professionChecked && !hasProfession && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-gray-200 w-[420px] max-w-[90vw] px-5 py-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#00aef7]/10 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-[#00aef7]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">One quick thing first...</h4>
                <p className="text-xs text-gray-500">What industry or profession are you in?</p>
              </div>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={industryInput}
                onChange={(e) => setIndustryInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !resolving) handleResolveProfession(); }}
                placeholder="e.g. Plumbing, Real Estate, Roofing..."
                className="eh-design-nodrag flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7]"
                disabled={resolving}
                autoFocus
              />
              <button
                type="button"
                onClick={handleResolveProfession}
                disabled={resolving || !industryInput.trim()}
                className={`eh-design-nodrag px-3 py-1.5 rounded-lg text-white text-xs font-medium transition ${
                  resolving || !industryInput.trim()
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#00aef7] hover:bg-[#0097d6]'
                }`}
              >
                {resolving ? (
                  <div className="flex items-center gap-1">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                    <span>Checking...</span>
                  </div>
                ) : 'Go'}
              </button>
            </div>

            {resolveError && (
              <p className="text-xs text-amber-600 mt-1.5">{resolveError}</p>
            )}
          </div>
        </div>
      )}

      <div
        style={{ zIndex: designCenterZ }}
      className="fixed inset-0 flex items-center justify-center pointer-events-none"
      aria-modal="true"
      role="dialog"
    >
      <Rnd
        cancel=".eh-design-nodrag"
        onMouseDownCapture={() => bringToFront('designCenter')}
        size={
          windowMode === 'max'
            ? { width: getMaxRect().width, height: getMaxRect().height }
            : { width: windowRect.width, height: windowRect.height }
        }
        position={
          windowMode === 'max'
            ? { x: getMaxRect().x, y: getMaxRect().y }
            : { x: windowRect.x, y: windowRect.y }
        }
        minWidth={600}
        minHeight={400}
        bounds="window"
        dragHandleClassName="eh-msg-header"
        disableDragging={windowMode === 'max'}
        enableResizing={
          windowMode === 'max'
            ? false
            : {
                bottomRight: true,
                bottomLeft: true,
                topRight: true,
                topLeft: true,
                left: true,
                right: true,
                bottom: true,
                top: true,
              }
        }
        onDragStop={(e, d) => {
          if (windowMode === 'max') return;
          setWindowRect((prev) => ({
            ...(prev || {}),
            x: d.x,
            y: d.y,
          }));
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          if (windowMode === 'max') return;
          setWindowRect({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
            x: position.x,
            y: position.y,
          });
        }}
        className="rounded-2xl shadow-2xl overflow-hidden flex flex-col bg-white pointer-events-auto"
      >
        <div
          ref={modalRef}
          className="relative w-full h-full flex flex-col"
        >
          {/* Header - Brand blue background with white text */}
          <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-2 border-b border-[#00aef7] bg-[#00aef7] text-white cursor-move eh-msg-header">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold tracking-tight">
                eHandshake AI-powered Design Center
              </h2>
              {/* Creation limit counter DISABLED for pre-launch — unlimited for all users */}
              {/* {creationInfo && creationInfo.limit !== null && (
                <span className="text-xs font-medium bg-white/20 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                  {creationInfo.activeCreationCount} / {creationInfo.limit} creation{creationInfo.limit === 1 ? '' : 's'}
                </span>
              )} */}
            </div>

            <div className="flex items-center gap-1">
              {isDesignCenterAdmin && (
                <button
                  type="button"
                  onClick={() => window.open('/admin/designcenter', '_blank', 'noopener,noreferrer')}
                  className="eh-design-nodrag inline-flex items-center justify-center rounded-md px-2 py-1 text-white hover:bg-white/15"
                  aria-label="Open Design Center Admin"
                  title="Open Design Center Admin"
                >
                  Admin
                </button>
              )}

              <button
                type="button"
                onClick={toggleMaximize}
                className="eh-design-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title={windowMode === 'max' ? 'Restore' : 'Maximize'}
              >
                {windowMode === 'max' ? <FaWindowRestore size={12} /> : <FaWindowMaximize size={12} />}
              </button>
              <button
                onClick={handleCloseClick}
                className="eh-design-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>


          {/* Tabs Row - All tabs in brand blue, active has underline */}
          <div className="shrink-0 flex items-center gap-0 px-4 py-0 bg-white border-b border-gray-200 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id)}
                className={`eh-design-nodrag px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors text-[#00aef7] ${
                  activeTab === tab.id
                    ? 'border-[#00aef7]'
                    : 'border-transparent hover:border-[#00aef7]/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Body Content */}
          <div className="flex-1 min-h-0 overflow-auto px-4 py-3 bg-[#f8fafc] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {showAdminReview ? (
              <AdminDesignCenterReview />
            ) : activeTab == null ? (
              <>
                {/* Content Header */}
                <div className="text-center mb-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-0">Content ideas for {professionName || 'your business'}</h3>
                  <p className="text-gray-600 text-sm">What are you trying to do right now?</p>
                </div>

                {/* Goal Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                  {goalCards.map((card) => (
                    <div
                      key={card.id}
                      className={`p-2 rounded-xl border-2 ${card.bgColor} ${card.borderColor} text-center flex flex-col items-center`}
                    >
                      <h4 className="text-base font-semibold text-gray-800 mb-1">{card.title}</h4>
                      <div className="my-1">{card.icon}</div>
                      <p className="text-xs text-gray-600">{card.subtitle}</p>
                      {card.links && card.links.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200/60 w-full flex flex-col gap-1">
                          {card.links.map((lnk) => (
                            <button
                              key={lnk.tabId}
                              type="button"
                              onClick={() => handleTabClick(lnk.tabId)}
                              className="eh-design-nodrag text-xs font-medium text-[#00aef7] hover:text-[#0097d6] hover:underline"
                            >
                              {lnk.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Bottom Section */}
                <div className="bg-[#eef4f8] rounded-xl p-4 text-center">

                  <p className="text-gray-700 font-medium text-sm mb-2">
                    Select a goal to see some AI-created content ideas for {professionName || 'your business'}:
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <svg className="w-4 h-4 text-[#00aef7]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      How-to videos & tips
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <svg className="w-4 h-4 text-[#00aef7]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      Trust-building stories
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <svg className="w-4 h-4 text-[#00aef7]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      Industry-specific content
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <svg className="w-4 h-4 text-[#00aef7]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      Engaging social media posts
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <DesignCenterWorkspace
                activeTabLabel={tabs.find((t) => t.id === activeTab)?.label}
                subcategoryKey={tabs.find((t) => t.id === activeTab)?.folderKey}
                onHasActiveWork={setHasActiveWork}
              />
            )}
          </div>

          {/* Tab Switch Confirmation Dialog */}
          {showSwitchConfirm && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 rounded-2xl">
              <div className="bg-white rounded-xl shadow-xl p-5 max-w-sm mx-4 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Switch Categories?</h4>
                <p className="text-xs text-gray-600 mb-4">
                  Switching categories will clear your current design. Are you sure?
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={cancelTabSwitch}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Stay Here
                  </button>
                  <button
                    type="button"
                    onClick={confirmTabSwitch}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600"
                  >
                    Switch & Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Close Confirmation Dialog */}
          {showCloseConfirm && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 rounded-2xl">
              <div className="bg-white rounded-xl shadow-xl p-5 max-w-sm mx-4 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Close Design Center?</h4>
                <p className="text-xs text-gray-600 mb-4">
                  You have an active design in progress. Closing will discard your current work.
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={cancelClose}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Keep Editing
                  </button>
                  <button
                    type="button"
                    onClick={confirmClose}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600"
                  >
                    Close & Discard
                  </button>
                </div>
              </div>
            </div>
          )}


        </div>
      </Rnd>
    </div>
    </>
  );
}
